// Finanzplanung — Vorsorge-Engine: Rentenlücke (Angestellte) und
// Pensionslücke (Beamte), portiert aus „Mandanten-Log II" (Sheets 10a/10b).
//
// Pure Functions ohne DB/IO; Beträge als Integer-Cents an den Rändern,
// intern float (Entgeltpunkte/Faktoren sind gebrochen), Rundung erst im
// Ergebnis. Alle Formeln 1:1 aus dem Numbers-Export verifiziert — die
// Unit-Tests rechnen die Original-Beispiele nach (Design-PDF S. 16/17).
//
// Parameter tragen die Original-Defaults (Stand 2021, z.B. BBG 85.200 €,
// Rentenfaktor West 34,19 €) und sind bewusst editierbar — die UI labelt
// den Stand und erlaubt Overrides pro User (in den Calculator-Inputs).

// === Rentenlücke (Angestellte, Sheet 10a) ===================================

export interface PensionParams {
  /** Rentenwert je Entgeltpunkt, €/Monat in Cents (West 2021: 34,19 €). */
  rentenfaktorCents: number
  /** ø-Jahreseinkommen brutto = 1 Entgeltpunkt, Cents (2021: 40.500 €). */
  avgAnnualIncomeCents: number
  /** Beitragsbemessungsgrenze Rente, brutto/Jahr in Cents (2021: 85.200 €). */
  bbgAnnualCents: number
  /** Beitragsbemessungsgrenze KV, brutto/Monat in Cents — Krankengeld-Deckel
   *  (2021: 4.837,50 €). */
  bbgKvMonthlyCents: number
  /** Inflation p.a. als (negativer) Bruch, Original: −0,015. */
  inflationRate: number
  retirementAge: number
}

export const PENSION_RENTENFAKTOR_WEST_CENTS = 3419
export const PENSION_RENTENFAKTOR_OST_CENTS = 3323

/** Eingabe-Grenzen der Vorsorge-Rechner — geteilt zwischen API-Zod und
 *  Client-Clamps, damit beide dieselbe Grenze sprechen (sonst produziert
 *  der Client 400er, die er nicht versteht — Review-Fund Paket 2). */
export const VORSORGE_BOUNDS = {
  ageMin: 14,
  ageMax: 100,
  pensionPointsMax: 300,
  ktgDailyMaxCents: 100_000,
  /** |Inflationsrate| als Bruch (±20 %). */
  inflationAbs: 0.2,
  retirementMin: 50,
  retirementMax: 75,
  serviceYearsMax: 60,
  besoldungsStufeMax: 12,
} as const

export const PENSION_DEFAULT_PARAMS: PensionParams = {
  rentenfaktorCents: PENSION_RENTENFAKTOR_WEST_CENTS,
  avgAnnualIncomeCents: 4_050_000,
  bbgAnnualCents: 8_520_000,
  bbgKvMonthlyCents: 483_750,
  inflationRate: -0.015,
  retirementAge: 67,
}

export interface PensionInputs {
  age: number
  /** Heutiges Netto/Brutto, monatlich in Cents. */
  netMonthlyCents: number
  grossMonthlyCents: number
  /** Bisherige Entgeltpunkte (gebrochen, z.B. 3.0). */
  pensionPoints: number
  /** Vorhandene BU-Rente, monatlich in Cents. */
  buPensionMonthlyCents: number
  /** Vorhandenes Krankentagegeld, €/Tag in Cents. */
  ktgDailyCents: number
  /** Weitere Renten, monatlich in Cents. */
  fundedPensionMonthlyCents: number
  capitalPensionMonthlyCents: number
  rentalIncomeMonthlyCents: number
}

export const PENSION_DEFAULT_INPUTS: PensionInputs = {
  age: 30,
  netMonthlyCents: 0,
  grossMonthlyCents: 0,
  pensionPoints: 0,
  buPensionMonthlyCents: 0,
  ktgDailyCents: 0,
  fundedPensionMonthlyCents: 0,
  capitalPensionMonthlyCents: 0,
  rentalIncomeMonthlyCents: 0,
}

export interface PensionResult {
  yearsToRetirement: number
  annualGrossCents: number
  totalPensionPoints: number
  /** Gesetzliche Rente / Erwerbsminderungsrente, brutto monatlich. */
  statutoryPensionGrossCents: number
  emPensionGrossCents: number
  /** Brutto gesamt inkl. geförderter/kapitalgebundener Rente + Miete. */
  pensionGrossTotalCents: number
  /** Persönlicher Steuer-/Abgabenfaktor (0,10–0,20). */
  taxFactor: number
  pensionNetCents: number
  /** Kaufkraft-Faktor, negativ (z.B. −0,4699 bei 42 Jahren × −1,5 %). */
  inflationFactor: number
  purchasingPowerCents: number
  /** Rentenlücke = Kaufkraft-Rente − heutiges Netto (negativ = Lücke). */
  gapCents: number
  /** „Aus 100 € werden:" in Cents (z.B. 5301). */
  from100Cents: number
  /** Kaufkraft-Verlust durch Inflation (negativ). */
  purchasingLossCents: number
  bu: {
    /** Lücken je Szenario vs. heutiges Netto (negativ = Lücke). */
    fullEmGapCents: number
    halfEmGapCents: number
    noneGapCents: number
  }
  sickPay: {
    krankengeldGrossCents: number
    krankengeldNetCents: number
    /** Lücke ohne Krankentagegeld (negativ = Lücke). */
    gapCents: number
    /** Lücke inkl. vorhandenem KTG (Original: KTG×30 + KG-Netto − Netto). */
    gapWithKtgCents: number
    /** Empfohlenes Krankentagegeld, €/Tag in Cents (aufgerundet auf volle €). */
    recommendedKtgDailyCents: number
  }
}

/** Steuer-/Abgabenfaktor aus dem Original: 2.000–6.000 € brutto → linear
 *  10–20 %, darüber 20 %, darunter 10 %. Erwartet Cents. */
export function pensionTaxFactor(grossMonthlyCents: number): number {
  const euros = grossMonthlyCents / 100
  if (euros >= 2000 && euros <= 6000) return 0.01 * (10 + (euros - 2000) * 0.0025)
  return euros > 6000 ? 0.2 : 0.1
}

/** Kaufkraft-Faktor: −(1 − (1+rate)^years) — negativ bei negativer Rate. */
export function inflationFactorFor(rate: number, years: number): number {
  const f = -(1 - (1 + rate) ** years)
  // −0 normalisieren (years=0 ergäbe sonst Object.is-untaugliches −0).
  return f === 0 ? 0 : f
}

export function computePensionGap(inputs: PensionInputs, params: PensionParams): PensionResult {
  const years = Math.max(params.retirementAge - inputs.age, 0)
  const annualGross = inputs.grossMonthlyCents * 12
  const cappedAnnual = Math.min(annualGross, params.bbgAnnualCents)
  const totalPoints =
    inputs.pensionPoints +
    (params.avgAnnualIncomeCents > 0 ? (cappedAnnual / params.avgAnnualIncomeCents) * years : 0)
  const statutoryGross = totalPoints * params.rentenfaktorCents
  const emGross = statutoryGross * 0.67
  const grossTotal =
    statutoryGross +
    inputs.fundedPensionMonthlyCents +
    inputs.capitalPensionMonthlyCents +
    inputs.rentalIncomeMonthlyCents
  const tax = pensionTaxFactor(grossTotal)
  const net = grossTotal * (1 - tax)
  const inflFactor = inflationFactorFor(params.inflationRate, years)
  const purchasing = net * (1 + inflFactor)

  // Krankengeld: min(70 % Brutto | 90 % Netto, KV-BBG), netto ×0,879.
  const seventyGross = inputs.grossMonthlyCents * 0.7
  const ninetyNet = inputs.netMonthlyCents * 0.9
  const kgGross = Math.min(
    seventyGross > ninetyNet ? ninetyNet : seventyGross,
    params.bbgKvMonthlyCents,
  )
  const kgNet = kgGross * 0.879
  const kgGap = kgNet - inputs.netMonthlyCents
  // Original: ROUNDUP(Lücke/(−30)) €/Tag — aufrunden auf volle Euro.
  const recommendedKtgDaily = kgGap < 0 ? Math.ceil(-kgGap / 30 / 100) * 100 : 0

  return {
    yearsToRetirement: years,
    annualGrossCents: Math.round(annualGross),
    totalPensionPoints: totalPoints,
    statutoryPensionGrossCents: Math.round(statutoryGross),
    emPensionGrossCents: Math.round(emGross),
    pensionGrossTotalCents: Math.round(grossTotal),
    taxFactor: tax,
    pensionNetCents: Math.round(net),
    inflationFactor: inflFactor,
    purchasingPowerCents: Math.round(purchasing),
    gapCents: Math.round(purchasing - inputs.netMonthlyCents),
    from100Cents: Math.round(10000 * (1 + inflFactor)),
    purchasingLossCents: Math.round(net * inflFactor),
    bu: {
      fullEmGapCents: Math.round(emGross + inputs.buPensionMonthlyCents - inputs.netMonthlyCents),
      halfEmGapCents: Math.round(
        emGross / 2 + inputs.buPensionMonthlyCents - inputs.netMonthlyCents,
      ),
      noneGapCents: Math.round(inputs.buPensionMonthlyCents - inputs.netMonthlyCents),
    },
    sickPay: {
      krankengeldGrossCents: Math.round(kgGross),
      krankengeldNetCents: Math.round(kgNet),
      gapCents: Math.round(kgGap),
      gapWithKtgCents: Math.round(inputs.ktgDailyCents * 30 + kgGap),
      recommendedKtgDailyCents: recommendedKtgDaily,
    },
  }
}

// === Pensionslücke (Beamte, Sheet 10b) ======================================

export interface CivilPensionParams {
  /** Inflation abzügl. Besoldungserhöhung, Original: −0,01. */
  inflationRate: number
  retirementAge: number
  /** Mindestversorgung, monatlich in Cents (Original: 1.800 €). */
  minPensionMonthlyCents: number
  /** ~ Netto-Quote auf das zukünftige Brutto (Original: 0,76). */
  netRatio: number
  /** Ruhegehalt-Quote auf das zukünftige ø-Brutto (Original: 0,70). */
  pensionRatio: number
  /** DU-Fall: Basis-Quote + Steigerung je Dienstjahr, Deckel (0,35/0,018/0,72). */
  duBaseRatio: number
  duPerServiceYear: number
  duMaxRatio: number
}

export const CIVIL_PENSION_DEFAULT_PARAMS: CivilPensionParams = {
  inflationRate: -0.01,
  retirementAge: 67,
  minPensionMonthlyCents: 180_000,
  netRatio: 0.76,
  pensionRatio: 0.7,
  duBaseRatio: 0.35,
  duPerServiceYear: 0.018,
  duMaxRatio: 0.72,
}

export interface CivilPensionInputs {
  /** Besoldungsgruppe + Stufe (Referenz-Tafel, reine Anzeige/Prefill). */
  besoldungsGroup: string
  besoldungsStufe: number
  age: number
  netMonthlyCents: number
  grossMonthlyCents: number
  /** Jahre ruhegehaltsfähiger Dienstzeit. */
  serviceYears: number
  /** Zukünftiges ø-Brutto der letzten 5 Dienstjahre, monatlich in Cents. */
  futureAvgGrossMonthlyCents: number
  duPensionMonthlyCents: number
  fundedPensionMonthlyCents: number
  capitalPensionMonthlyCents: number
  rentalIncomeMonthlyCents: number
}

export const CIVIL_PENSION_DEFAULT_INPUTS: CivilPensionInputs = {
  besoldungsGroup: 'A9',
  besoldungsStufe: 5,
  age: 30,
  netMonthlyCents: 0,
  grossMonthlyCents: 0,
  serviceYears: 0,
  futureAvgGrossMonthlyCents: 0,
  duPensionMonthlyCents: 0,
  fundedPensionMonthlyCents: 0,
  capitalPensionMonthlyCents: 0,
  rentalIncomeMonthlyCents: 0,
}

export interface CivilPensionResult {
  yearsToRetirement: number
  /** ~ Zukünftiges Netto = zukünftiges ø-Brutto × netRatio. */
  futureNetMonthlyCents: number
  /** Beamten-Pension brutto = max(ø-Brutto × pensionRatio, Mindestversorgung). */
  pensionGrossCents: number
  pensionGrossTotalCents: number
  taxFactor: number
  pensionNetCents: number
  inflationFactor: number
  purchasingPowerCents: number
  /** Pensionslücke = Kaufkraft-Pension − heutiges Netto (wie Original). */
  gapCents: number
  from100Cents: number
  purchasingLossCents: number
  du: {
    /** Ruhegehalt brutto = min(heutiges Brutto × (Basis + Jahre×Steigerung), Deckel). */
    ruhegehaltGrossCents: number
    /** Lücke = (Ruhegehalt + DU-Rente) − heutiges Netto. */
    gapCents: number
    /** Zukünftiger Besoldungsausfall = heutiges Netto − zukünftiges Netto. */
    besoldungsausfallCents: number
  }
}

export function computeCivilPensionGap(
  inputs: CivilPensionInputs,
  params: CivilPensionParams,
): CivilPensionResult {
  const years = Math.max(params.retirementAge - inputs.age, 0)
  const futureNet = inputs.futureAvgGrossMonthlyCents * params.netRatio
  const pensionGross = Math.max(
    inputs.futureAvgGrossMonthlyCents * params.pensionRatio,
    params.minPensionMonthlyCents,
  )
  const grossTotal =
    pensionGross +
    inputs.fundedPensionMonthlyCents +
    inputs.capitalPensionMonthlyCents +
    inputs.rentalIncomeMonthlyCents
  const tax = pensionTaxFactor(grossTotal)
  const net = grossTotal * (1 - tax)
  const inflFactor = inflationFactorFor(params.inflationRate, years)
  const purchasing = net * (1 + inflFactor)

  const ruhegehalt = Math.min(
    inputs.grossMonthlyCents * (params.duBaseRatio + inputs.serviceYears * params.duPerServiceYear),
    inputs.grossMonthlyCents * params.duMaxRatio,
  )

  return {
    yearsToRetirement: years,
    futureNetMonthlyCents: Math.round(futureNet),
    pensionGrossCents: Math.round(pensionGross),
    pensionGrossTotalCents: Math.round(grossTotal),
    taxFactor: tax,
    pensionNetCents: Math.round(net),
    inflationFactor: inflFactor,
    purchasingPowerCents: Math.round(purchasing),
    gapCents: Math.round(purchasing - inputs.netMonthlyCents),
    from100Cents: Math.round(10000 * (1 + inflFactor)),
    purchasingLossCents: Math.round(net * inflFactor),
    du: {
      ruhegehaltGrossCents: Math.round(ruhegehalt),
      gapCents: Math.round(ruhegehalt + inputs.duPensionMonthlyCents - inputs.netMonthlyCents),
      besoldungsausfallCents: Math.round(inputs.netMonthlyCents - futureNet),
    },
  }
}

// === Besoldungstafel (Referenz, Stand 2021 — aus dem Original-Workbook) =====

export interface BesoldungRow {
  group: string
  /** Erste vorhandene Stufe (Tafel beginnt je Gruppe versetzt). */
  startStufe: number
  /** Monatsbrutto je Stufe in Cents, ab startStufe aufsteigend. */
  valuesCents: number[]
}

const b = (euros: number) => Math.round(euros * 100)

export const BESOLDUNGSTAFEL_2021: readonly BesoldungRow[] = [
  {
    group: 'A5',
    startStufe: 1,
    valuesCents: [
      2409.36, 2488.39, 2549.8, 2611.22, 2672.63, 2734.05, 2795.46, 2856.88, 2918.32, 2979.75,
    ].map(b),
  },
  {
    group: 'A6',
    startStufe: 1,
    valuesCents: [
      2461.24, 2528.68, 2596.11, 2663.53, 2730.97, 2798.42, 2865.87, 2933.29, 3000.72, 3068.13,
    ].map(b),
  },
  {
    group: 'A7',
    startStufe: 1,
    valuesCents: [
      2530.34, 2590.22, 2674.05, 2757.92, 2841.77, 2925.57, 3009.45, 3069.3, 3129.2, 3189.11,
    ].map(b),
  },
  {
    group: 'A8',
    startStufe: 2,
    valuesCents: [
      2675.29, 2746.93, 2854.37, 2961.81, 3069.26, 3176.73, 3248.35, 3319.98, 3391.63, 3463.24,
    ].map(b),
  },
  {
    group: 'A9',
    startStufe: 2,
    valuesCents: [
      2802.97, 2872.6, 2985.9, 3099.18, 3212.49, 3325.79, 3403.63, 3481.58, 3559.45, 3637.32,
    ].map(b),
  },
  {
    group: 'A10',
    startStufe: 2,
    valuesCents: [
      3003.56, 3100.34, 3245.47, 3390.64, 3535.79, 3680.96, 3777.73, 3874.97, 3973.94, 4072.94,
    ].map(b),
  },
  {
    group: 'A11',
    startStufe: 3,
    valuesCents: [
      3416.32, 3560.74, 3705.18, 3849.63, 3997.29, 4095.76, 4194.27, 4294.12, 4394.58, 4495.09,
    ].map(b),
  },
  {
    group: 'A12',
    startStufe: 4,
    valuesCents: [
      3824.06, 3999.52, 4175.69, 4354.35, 4474.14, 4593.93, 4713.75, 4833.57, 4953.31,
    ].map(b),
  },
  {
    group: 'A13',
    startStufe: 5,
    valuesCents: [4463.4, 4657.4, 4851.42, 4980.79, 5110.13, 5239.5, 5368.88, 5498.22].map(b),
  },
  {
    group: 'A14',
    startStufe: 5,
    valuesCents: [4739.3, 4990.92, 5242.5, 5410.25, 5577.98, 5745.74, 5913.48, 6081.24].map(b),
  },
  {
    group: 'A15',
    startStufe: 6,
    valuesCents: [5475.22, 5751.85, 5973.14, 6194.46, 6415.79, 6637.11, 6858.41].map(b),
  },
  {
    group: 'A16',
    startStufe: 6,
    valuesCents: [6033.41, 6353.31, 6609.29, 6865.26, 7121.18, 7377.16, 7633.11].map(b),
  },
]

/** Monatsbrutto (Cents) für Gruppe+Stufe, null wenn außerhalb der Tafel. */
export function besoldungFor(group: string, stufe: number): number | null {
  const row = BESOLDUNGSTAFEL_2021.find((r) => r.group === group)
  if (!row) return null
  const idx = stufe - row.startStufe
  if (idx < 0 || idx >= row.valuesCents.length) return null
  return row.valuesCents[idx]
}
